import { useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import sortBy from 'lodash.sortby';
import { Typography } from '@components/Typography';
import { Select } from '@components/Select';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Input } from '@components/Input';
import { Switch } from '@components/Switch';
import Button from '@components/Button';
import { LOCALE } from '@constants/locale';
import { ExternalRoutes } from '@constants/routes';
import { Industry } from '@constants/account';
import { ExternalLink } from '@components/Link';
import FixedFooter from '@components/Layout/FixedFooter';
import { handleApiError } from '@helpers/api';
import {
    isPaidMedium,
    isShoppingCampaign,
    listingLinkPreview,
    TRACKING_TAG_SLUG,
    TRACKING_TAGS_DEFAULT_CAMPAIGN,
    TRACKING_TAGS_DEFAULT_MEDIUM,
    TRACKING_TAGS_MAX_LENGTH,
    TRACKING_TAGS_SOURCES,
    TRACKING_TAGS_STORE_CODE_SAMPLE,
} from '@helpers/trackingTags';
import { accountActions, alertActions, authActions } from '@actions';
import { useTranslation, useYupValidationResolver, useThrottle, useCopyToClipboard } from '@hooks';
import McpConnections from './McpConnections';

export const Container = styled('div')({
    marginBottom: 65,
    '@media (max-width: 1024px)': {
        marginBottom: 80,
    },
});

const Form = styled('div')({
    marginBottom: 24,
    maxWidth: '698px',
    display: 'grid',
    gridGap: '16px 24px',
    gridTemplateColumns: '1fr 1fr',
    '@media (max-width: 1024px)': {
        gridTemplateColumns: '1fr',
        maxWidth: 'none',
    },
});

const Footer = styled(FixedFooter)({
    '@media (max-width: 1024px)': {
        '& button': {
            width: '100%',
        },
    },
});

export const GeneralSettings = () => {
    const { t, i18n } = useTranslation();

    const { onCopy } = useCopyToClipboard();
    const dispatch = useDispatch();

    const account = useSelector(state => state.account?.account);
    const user = useSelector(state => state.auth?.user);
    const userIsAccountOwner = user.id === account.ownerId;

    const trackingTag = yup
        .string()
        .required('validation.required')
        .max(TRACKING_TAGS_MAX_LENGTH, 'validation.maxLength')
        .matches(TRACKING_TAG_SLUG, 'validation.trackingTagSlug');
    const AccountSchema = yup.object().shape({
        accountName: yup.string().required('validation.required').max(255, 'validation.maxLength'),
        industry: yup.number().required('validation.required'),
        trackingTagsEnabled: yup.boolean(),
        trackingTagsMedium: trackingTag.test('paid', 'validation.trackingTagPaidMedium', value => !isPaidMedium(value)),
        trackingTagsCampaign: trackingTag.test(
            'shopping',
            'validation.trackingTagShoppingCampaign',
            value => !isShoppingCampaign(value),
        ),
    });

    const resolver = useYupValidationResolver(AccountSchema);

    const {
        getValues,
        handleSubmit,
        setValue,
        control,
        reset,
        watch,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver: resolver,
        defaultValues: {
            accountName: '',
            ownerId: '',
            locale: '',
            apiKey: '',
            industry: '',
            trackingTagsEnabled: false,
            trackingTagsMedium: TRACKING_TAGS_DEFAULT_MEDIUM,
            trackingTagsCampaign: TRACKING_TAGS_DEFAULT_CAMPAIGN,
        },
    });

    useEffect(() => {
        if (account) {
            setValue('accountName', account?.accountName || '', { shouldValidate: true });
            setValue('ownerId', account?.ownerId || '', { shouldValidate: true });
            setValue('locale', account?.locale ?? '', { shouldValidate: true });
            setValue('apiKey', account?.apiKey || '', { shouldValidate: true });
            setValue('industry', account?.industry, { shouldValidate: true });
            setValue('trackingTagsEnabled', !!account?.trackingTagsEnabled, { shouldValidate: true });
            setValue('trackingTagsMedium', account?.trackingTagsMedium || TRACKING_TAGS_DEFAULT_MEDIUM, {
                shouldValidate: true,
            });
            setValue('trackingTagsCampaign', account?.trackingTagsCampaign || TRACKING_TAGS_DEFAULT_CAMPAIGN, {
                shouldValidate: true,
            });
        }
    }, [account]);

    const userOptions = useMemo(
        () =>
            account.users
                .filter(u => u.isOwner || u.teams?.some(t => t.permissions.includes('settings:full')))
                .map(u => ({
                    value: u.id,
                    label: `${u?.firstName} ${u?.lastName}`,
                })),
        [account],
    );

    const localeOptions = sortBy(
        [
            { value: LOCALE.BRAZIL, label: 'Brazil' },
            { value: LOCALE.INTERNATIONAL, label: 'International' },
            { value: LOCALE.PORTUGAL, label: 'Portugal' },
            { value: LOCALE.SPAIN, label: 'Spain' },
            { value: LOCALE.UKRAINE, label: 'Ukraine' },
            { value: LOCALE.UNITED_KINGDOM, label: 'United Kingdom' },
            { value: LOCALE.POLAND, label: 'Poland' },
            { value: LOCALE.GERMANY, label: 'Germany' },
            { value: LOCALE.UNITED_STATES, label: 'United States' },
            { value: LOCALE.MEXICO, label: 'Mexico' },
        ],
        'label',
    );

    const industryOptions = Object.entries(Industry).map(([key, value]) => ({
        value,
        label: t(`industry.${key}`),
    }));

    const [onCopyClick] = useThrottle(value => onCopy(value), 1000);
    const [trackingTagsEnabled, trackingTagsMedium, trackingTagsCampaign] = watch([
        'trackingTagsEnabled',
        'trackingTagsMedium',
        'trackingTagsCampaign',
    ]);

    const restoreTrackingTags = () => {
        setValue('trackingTagsMedium', account.trackingTagsMedium || TRACKING_TAGS_DEFAULT_MEDIUM, {
            shouldValidate: true,
        });
        setValue('trackingTagsCampaign', account.trackingTagsCampaign || TRACKING_TAGS_DEFAULT_CAMPAIGN, {
            shouldValidate: true,
        });
    };

    const onSubmit = async () => {
        const values = getValues();
        try {
            await dispatch(
                accountActions.updateAccount({
                    ...account,
                    ...values,
                }),
            );
            if (values.ownerId !== user?.id) {
                await dispatch(authActions.getCurrentUser());
            }
            dispatch(alertActions.success(t('alertMessages.updateSuccess')));
            reset(values);
        } catch (error) {
            handleApiError(error, 'alertMessages.updateFail');
        }
    };

    if (!account) return null;

    return (
        <Container>
            <Typography variant="h6" sx={{ marginBottom: '24px' }}>
                {t('SettingsGeneral.accountNameOwnership')}
            </Typography>
            <Form>
                <Controller
                    control={control}
                    name="accountName"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field }}
                            label={t('SettingsGeneral.accountName')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message, { count: 255 }) : null}
                            required
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="ownerId"
                    render={({ field, ref }) => (
                        <Select
                            {...field}
                            value={String(getValues('ownerId'))}
                            ref={ref}
                            label={t('SettingsGeneral.accountOwner')}
                            options={userOptions}
                            disabled={!userIsAccountOwner}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="locale"
                    render={({ field, ref }) => (
                        <Select
                            {...field}
                            ref={ref}
                            label={t('SettingsGeneral.accountLocale')}
                            options={localeOptions}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="industry"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Select
                            {...field}
                            ref={ref}
                            label={t('common.industry')}
                            options={industryOptions}
                            required
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="apiKey"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field }}
                            onAddonClick={() => onCopyClick(getValues('apiKey'))}
                            addonComponent={<ContentCopyIcon sx={{ fontSize: 18, color: 'var(--text-secondary)' }} />}
                            label="X-Api-Key"
                            error={!!error}
                            helperText={
                                error ? (
                                    error.message
                                ) : (
                                    <ExternalLink variant="caption" href={ExternalRoutes.ApiIntegrationDocs}>
                                        {t('SettingsGeneral.apiDocumentation')}
                                    </ExternalLink>
                                )
                            }
                            disabled
                        />
                    )}
                />
            </Form>
            <Typography variant="h6" sx={{ marginBottom: '8px' }}>
                {t('SettingsGeneral.linkTracking')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)', marginBottom: '16px', maxWidth: 698 }}>
                {t('SettingsGeneral.linkTrackingDescription')}{' '}
                <ExternalLink
                    variant="body2"
                    underline="hover"
                    href={ExternalRoutes.LearnMore[i18n.language].linkTracking}
                >
                    {t('common.learnMore')}
                </ExternalLink>
            </Typography>
            <Form>
                <Controller
                    control={control}
                    name="trackingTagsEnabled"
                    render={({ field }) => (
                        <Switch
                            checked={!!field.value}
                            handleChange={e => {
                                field.onChange(e.target.checked);
                                if (!e.target.checked) {
                                    restoreTrackingTags();
                                }
                            }}
                            label={t('SettingsGeneral.trackingTagsEnabled')}
                            sx={{ gridColumn: '1 / -1' }}
                        />
                    )}
                />
                {trackingTagsEnabled && (
                    <>
                        <Input
                            inputProps={{ value: TRACKING_TAGS_SOURCES.join(', '), readOnly: true }}
                            label={t('SettingsGeneral.trackingTagsSource')}
                            fullWidth
                            disabled
                        />
                        <Controller
                            control={control}
                            name="trackingTagsMedium"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field }}
                                    label={t('SettingsGeneral.trackingTagsMedium')}
                                    fullWidth
                                    required
                                    error={!!error}
                                    helperText={error ? t(error.message, { count: TRACKING_TAGS_MAX_LENGTH }) : null}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="trackingTagsCampaign"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field }}
                                    label={t('SettingsGeneral.trackingTagsCampaign')}
                                    fullWidth
                                    required
                                    error={!!error}
                                    helperText={error ? t(error.message, { count: TRACKING_TAGS_MAX_LENGTH }) : null}
                                />
                            )}
                        />
                        <Input
                            inputProps={{ value: TRACKING_TAGS_STORE_CODE_SAMPLE, readOnly: true }}
                            label={t('SettingsGeneral.trackingTagsContent')}
                            fullWidth
                            disabled
                        />
                        <Typography
                            variant="caption"
                            sx={{ color: 'var(--text-secondary)', gridColumn: '1 / -1', wordBreak: 'break-all' }}
                        >
                            {t('SettingsGeneral.trackingTagsPreview')}:{' '}
                            {listingLinkPreview({
                                website: 'https://www.example.com',
                                source: 'google',
                                medium: trackingTagsMedium,
                                campaign: trackingTagsCampaign,
                                storeCode: TRACKING_TAGS_STORE_CODE_SAMPLE,
                            })}
                        </Typography>
                    </>
                )}
            </Form>
            <McpConnections accountId={account.id} />
            <Footer>
                <Button
                    onClick={handleSubmit(onSubmit)}
                    variant="contained"
                    disabled={isSubmitting || !isValid || !isDirty}
                >
                    {t('buttons.saveChanges')}
                </Button>
            </Footer>
        </Container>
    );
};
